import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { api, ApiError, type Me, type Realm } from "./api";
import { useTheme } from "./theme";

interface Ctx {
  me: Me;
  reload: () => Promise<void>;
  logout: () => Promise<void>;
}
const MeCtx = createContext<Ctx | null>(null);
export const useMe = () => {
  const c = useContext(MeCtx);
  if (!c) throw new Error("useMe fora do RealmGuard");
  return c;
};

/** Cada área (PF, PJ, Admin) tem a SUA sessão: sem sessão válida do próprio realm, volta ao login desse realm. */
export function RealmGuard({ realm, children }: { realm: Realm; children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<"loading" | "anon" | "error" | "ok">("loading");
  const { setPref } = useTheme();

  const load = useCallback(async () => {
    try {
      const m = await api<Me>("GET", `/api/${realm}/auth/me`);
      setMe(m);
      setPref(m.preferences.theme); // a preferência salva do usuário prevalece neste navegador
      setState("ok");
    } catch (e) {
      setState(e instanceof ApiError && e.status === 401 ? "anon" : "error");
    }
  }, [realm, setPref]);
  useEffect(() => { void load(); }, [load]);

  const logout = useCallback(async () => {
    try { await api("POST", `/api/${realm}/auth/logout`, {}); } finally { setMe(null); setState("anon"); }
  }, [realm]);

  if (state === "loading") return <div style={{ padding: 32 }} className="muted">Carregando…</div>;
  if (state === "anon") return <Navigate to={`/${realm}/entrar`} replace />;
  if (state === "error" || !me) return <div style={{ padding: 32 }} className="notice error">Serviço indisponível no momento. Tente novamente em instantes.</div>;
  return <MeCtx.Provider value={{ me, reload: load, logout }}>{children}</MeCtx.Provider>;
}
